import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Mail, MapPin, Phone, ArrowRight, Loader2 } from "lucide-react";
import Reveal from "../Reveal";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Inloopdouche",
  "Complete badkamer",
  "Toilet (particulier)",
  "Toiletten (zakelijk)",
  "Maatwerk renovatie",
  "Anders",
];

export default function Contact() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim());
    if (form.name.trim().length < 2) {
      toast.error("Vul uw volledige naam in (minimaal 2 tekens).");
      return;
    }
    if (!emailOk) {
      toast.error(
        "Vul een geldig e-mailadres in, bijvoorbeeld naam@bedrijf.nl."
      );
      return;
    }
    if (form.message.trim().length < 5) {
      toast.error(
        "Uw bericht is te kort — beschrijf in een paar woorden uw wensen."
      );
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Bedankt! We nemen spoedig contact met u op.");
      setForm({
        name: "",
        email: "",
        phone: "",
        project_type: "",
        message: "",
      });
      navigate("/bedankt", { state: { submitted: true } });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "Verzenden mislukt. Probeer het opnieuw of bel ons direct.";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        const field = detail[0]?.loc?.slice(-1)?.[0];
        const labels = {
          name: "naam",
          email: "e-mailadres",
          phone: "telefoonnummer",
          project_type: "type project",
          message: "bericht",
        };
        msg = `Controleer uw ${labels[field] || "invoer"} en probeer opnieuw.`;
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#F5F7FB] py-24 md:py-32"
    >
      <Toaster position="top-center" richColors closeButton />
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <Reveal className="lg:col-span-5">
          <span className="tag-overline">05 — Contact</span>
          <h2 className="mt-4 text-5xl md:text-6xl text-[#0A0A0A] leading-[1.02] uppercase tracking-tight">
            Plan uw gratis
            <br />
            <span className="text-[#1E5BC6]">adviesgesprek.</span>
          </h2>
          <p className="mt-6 text-lg text-[#5A5A5A] max-w-md">
            Laat uw gegevens achter — we nemen binnen één werkdag contact op om
            een afspraak in te plannen, bij u thuis of op locatie.
          </p>

          <div className="mt-10 space-y-5">
            <InfoRow
              icon={Phone}
              label="Bel direct"
              value="+31 6 87445044"
            />
            <InfoRow
              icon={Mail}
              label="E-mail"
              value="info@master-bathrooms.nl"
            />
            <InfoRow icon={MapPin} label="Werkgebied" value="Heel Nederland" />
          </div>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={120}>
          <form
            onSubmit={submit}
            noValidate
            data-testid="contact-form"
            className="bg-white border border-[#E5E7EB] p-8 md:p-12"
          >
            {done ? (
              <div data-testid="contact-success" className="py-12 text-center">
                <div className="text-3xl md:text-4xl text-[#0A0A0A] uppercase tracking-tight font-extrabold">
                  Dank u wel.
                </div>
                <div className="brand-rule mx-auto mt-5" />
                <p className="mt-6 text-[#5A5A5A] max-w-md mx-auto">
                  Uw aanvraag is succesvol verzonden. Een van onze adviseurs
                  neemt binnen één werkdag contact met u op.
                </p>
                <button
                  data-testid="contact-reset"
                  type="button"
                  onClick={() => setDone(false)}
                  className="mt-8 btn-outline-mb px-7 py-3 text-xs"
                >
                  Nieuw bericht versturen
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Naam *">
                    <Input
                      data-testid="contact-input-name"
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      placeholder="Uw volledige naam"
                      className="rounded-sm h-12 border-[#E5E7EB] focus-visible:ring-[#1E5BC6]"
                    />
                  </Field>
                  <Field label="E-mail *">
                    <Input
                      data-testid="contact-input-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      placeholder="naam@voorbeeld.nl"
                      className="rounded-sm h-12 border-[#E5E7EB] focus-visible:ring-[#1E5BC6]"
                    />
                  </Field>
                  <Field label="Telefoon">
                    <Input
                      data-testid="contact-input-phone"
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                      placeholder="06 — optioneel"
                      className="rounded-sm h-12 border-[#E5E7EB] focus-visible:ring-[#1E5BC6]"
                    />
                  </Field>
                  <Field label="Type project">
                    <Select
                      value={form.project_type}
                      onValueChange={(v) => set("project_type", v)}
                    >
                      <SelectTrigger
                        data-testid="contact-select-trigger"
                        className="rounded-sm h-12 border-[#E5E7EB] focus:ring-[#1E5BC6]"
                      >
                        <SelectValue placeholder="Selecteer een optie" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROJECT_TYPES.map((p) => (
                          <SelectItem
                            key={p}
                            value={p}
                            data-testid={`contact-select-${p}`}
                          >
                            {p}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="mt-5">
                  <Field label="Uw bericht *">
                    <Textarea
                      data-testid="contact-input-message"
                      rows={5}
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      placeholder="Vertel ons kort over uw wensen, ruimte en eventuele planning…"
                      className="rounded-sm border-[#E5E7EB] focus-visible:ring-[#1E5BC6]"
                    />
                  </Field>
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="mt-8 btn-primary-mb w-full md:w-auto px-8 py-4 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Verzenden…
                    </>
                  ) : (
                    <>
                      Aanvraag versturen
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
                <p className="mt-4 text-xs text-[#5A5A5A]">
                  Door te verzenden gaat u akkoord met telefonisch of per e-mail
                  contact over uw aanvraag.
                </p>
              </>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.22em] text-[#0A0A0A] mb-2 font-bold">
        {label}
      </span>
      {children}
    </label>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 bg-[#E9F0FB] flex items-center justify-center text-[#1E5BC6]">
        <Icon size={18} strokeWidth={1.8} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#5A5A5A] font-bold">
          {label}
        </div>
        <div className="text-base text-[#0A0A0A] mt-0.5 font-semibold">
          {value}
        </div>
      </div>
    </div>
  );
}
